<template>
    <div
        class="flex flex-1 flex-col"
        data-page
    >
        <div
            v-if="showLoadingState"
            class="flex flex-1 items-center justify-center py-16"
        >
            <div class="border-primary h-8 w-8 animate-spin rounded-full border-b-2" />
        </div>

        <!-- Show gate based on type if homepage is protected -->
        <template v-else-if="showGate">
            <!-- SAML SSO Gate -->
            <GatesSamlGate
                v-if="isSamlGate && hasSamlProviders"
                :gate-uid="entry?.authorisedContent?.gateUid"
                :providers="samlProviders"
                :show-title="entry?.authorisedContent?.showTitle ?? false"
                :message="entry?.authorisedContent?.message"
                :entry-title="entry?.title"
            />

            <!-- Password Gate -->
            <GatesPasswordGate
                v-else-if="isPasswordGate"
                :is-protected="entry.authorisedContent.isProtected"
                :is-authorized="entry.authorisedContent.isAuthorized"
                :requires-token="entry.authorisedContent.requiresToken"
                :gate-uid="entry.authorisedContent.gateUid"
                :gate-type="entry.authorisedContent.gateType"
                :gate-name="entry.authorisedContent.gateName"
                :show-title="entry.authorisedContent.showTitle ?? false"
                :message="entry.authorisedContent.message"
                :entry-title="entry.title"
                @authorized="handleAuthorized"
            />
        </template>

        <!-- Only show content if NOT protected or IS authorized -->
        <template v-else>
            <!-- Add logout button when content is protected, authorized, and logout is enabled -->
            <GatesLogoutButton
                v-if="
                    entry.authorisedContent?.enableLogoutButton === true &&
                    entry.authorisedContent?.isAuthorized &&
                    (entry.authorisedContent?.gateType === 'saml' || entry.authorisedContent?.gateType === 'password')
                "
                :gate-uid="entry.authorisedContent.gateUid"
                :gate-type="entry.authorisedContent.gateType"
                :return-url="route.fullPath"
                @logged-out="handleLoggedOut"
            />

            <Banner v-if="!entry?.hideBanner" />

            <Blocks />
        </template>

        <PreFooter />

        <Popup />
    </div>
</template>

<script setup>
    const entryStore = useEntryStore()
    const route = useRoute()
    const { entry, seomatic, seoHeadScripts, seoBodyScripts, seoBodyNoscripts } = storeToRefs(entryStore)

    const isProtected = computed(() => {
        return entry.value?.authorisedContent?.isProtected && !entry.value?.authorisedContent?.isAuthorized
    })

    const pageGateUid = computed(() => entry.value?.authorisedContent?.gateUid ?? null)

    const { showLoadingState, showGate, handleAuthorized, handleLoggedOut } = useProtectedPageGate({
        isProtected,
        gateUid: pageGateUid,
        reloadEntry: () => entryStore.reloadEntry(),
        onReloadError: (error, phase) => {
            console.error(`Failed to reload homepage during ${phase}:`, error)
        },
    })

    /**
     * Check if the gate is a SAML SSO gate
     */
    const isSamlGate = computed(() => {
        return entry.value?.authorisedContent?.gateType === 'saml'
    })

    /**
     * Check if the gate is a password gate
     */
    const isPasswordGate = computed(() => {
        return entry.value?.authorisedContent?.gateType === 'password'
    })

    /**
     * Get SAML providers array
     */
    const samlProviders = computed(() => {
        const providers = entry.value?.authorisedContent?.providers
        return Array.isArray(providers) ? providers : []
    })

    /**
     * Check if SAML gate has any providers
     */
    const hasSamlProviders = computed(() => {
        return samlProviders.value.length > 0
    })

    const seoTitle = computed(() => {
        const titleContainer = JSON.parse(seomatic.value?.metaTitleContainer ?? '{}')
        return get(titleContainer, 'title.title', entry.value?.title)
    })

    const metaTags = computed(() => {
        return flatMap(JSON.parse(seomatic.value?.metaTagContainer ?? '[]'))
    })

    useSeoMeta({
        title: seoTitle.value,
    })

    useHead({
        meta: metaTags.value,
        script: [...seoHeadScripts.value, ...seoBodyScripts.value],
        noscript: seoBodyNoscripts.value,
    })
</script>
